import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {Box, Link, Typography} from '@mui/material';
import {Autocomplete, LoadScriptNext} from '@react-google-maps/api';
import {useTranslation} from 'react-i18next';

import {AllowedCountryCodesSignIn} from '@/app/auth/constants/AllowedPhoneNumbers';
import {
  AppButton,
  AppButtonVariant,
  PhoneInputTextField,
} from '@/app/shared-components';
import InputTextField from '@/app/shared-components/InputTextField/InputTextField';
import {
  allowedAutocompleteTypes,
  defaultAddressCountry,
  parseGooglePlaceToAddress,
} from '@/app/utils/AddressUtils/AddressUtils';
import {ENV} from '@/constants/EnvConstants';
import {COLORS} from '@/styles/colors';
import {validateNameInput} from '@/utils/validateNames';

import {DEFAULT_COUNTRY, libraries} from './models/constants';
import {BillingAddressFormValues} from './models/interfaces';

import './styles/billingAddressFormMobile.scss';

export type BillingAddressFormRef = {
  expand: () => void;
};

type BillingAddressFormProps = {
  onSubmit: (billingInfo: BillingAddressFormValues) => void;
  onCollapsedChange?: () => void;
  startCollapsed: boolean;
  onChange?: (v: BillingAddressFormValues) => void;
  formValue?: BillingAddressFormValues;
  disableButtonContinue: boolean;
  simplified?: boolean;
};

const BillingAddressForm = forwardRef<
  BillingAddressFormRef,
  BillingAddressFormProps
>(
  (
    {
      onSubmit,
      onCollapsedChange,
      startCollapsed,
      formValue,
      onChange,
      disableButtonContinue = false,
      simplified = false,
    },
    ref,
  ) => {
    const {t} = useTranslation();

    const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(
      null,
    );

    const [emailError, setEmailError] = useState<boolean>(false);

    const [collapsed, setCollapsed] = useState<boolean>(startCollapsed);

    const [internalBilling, setInternalBilling] =
      useState<BillingAddressFormValues>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        postalCode: '',
        country: DEFAULT_COUNTRY,
      });

    const billing = useMemo(
      () => formValue ?? internalBilling,
      [formValue, internalBilling],
    );

    const setField = useCallback(
      (patch: Partial<BillingAddressFormValues>) => {
        const base = formValue ?? internalBilling;
        const next = {...base, ...patch};
        onChange ? onChange(next) : setInternalBilling(next);
      },
      [formValue, internalBilling, onChange],
    );

    const handlePlaceChanged = useCallback(() => {
      const ac = autoCompleteRef.current;
      if (!ac) return;

      const place = ac.getPlace();
      if (!place) return;

      const parsed = parseGooglePlaceToAddress(place);
      const formatted = (place as any)?.formatted_address as string | undefined;

      setField({
        line1: parsed.addressLine1 ?? formatted ?? billing.line1,
        city: parsed.city ?? billing.city,
        state: parsed.region ?? billing.state,
        postalCode: parsed.zipCode ?? billing.postalCode,
      });
    }, [billing, setField]);

    const loadingGooglePlacesElement = useMemo(
      () => (
        <div>{t('payment_link.form.loading_google_places') || 'Loading…'}</div>
      ),
      [t],
    );
    const normalizeBilling = (
      b: BillingAddressFormValues,
    ): BillingAddressFormValues => {
      const trim = (s: string) => (s ? s.trim() : '');
      return {
        firstName: trim(b.firstName),
        lastName: trim(b.lastName),
        email: trim(b.email),
        phone: trim(b.phone),
        line1: trim(b.line1),
        line2: trim(b.line2),
        city: trim(b.city),
        state: trim(b.state),
        postalCode: trim(b.postalCode),
        country: b.country || DEFAULT_COUNTRY,
      };
    };

    const handleSubmit = useCallback(() => {
      const formNormalized = normalizeBilling(billing);
      onSubmit(formNormalized);
      setCollapsed(false);
    }, [billing, onSubmit]);

    const handleExpandFromBack = useCallback(() => {
      setCollapsed(true);
      onCollapsedChange?.();
    }, [onCollapsedChange]);

    useImperativeHandle(ref, () => ({
      expand: handleExpandFromBack,
    }));

    const isFormValid = useMemo(() => {
      const v = normalizeBilling(billing);
      return (
        !!v.firstName &&
        !!v.lastName &&
        !!v.email &&
        !emailError &&
        (simplified || !!v.phone) &&
        !!v.line1 &&
        !!v.city &&
        !!v.state &&
        !!v.postalCode
      );
    }, [billing, emailError, simplified]);

    if (!collapsed) {
      return (
        <Box
          className="billing-contact-form-collapsed"
          sx={{display: 'flex', justifyContent: 'flex-start', mt: 1}}>
          <Link
            component="button"
            onClick={handleExpandFromBack}
            underline="hover"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 14,
              fontWeight: 400,
              lineHeight: '20px',
              color: COLORS.CONTENT_LIGHT_TERTIARY,
              textDecoration: 'underline',
            }}>
            <ChevronLeftIcon sx={{fontSize: 18, lineHeight: 0}} />
            {simplified
              ? t('payment_link_v2.form.back_button')
              : t('payment_link_v2.form.back_button_edit_address')}
          </Link>
        </Box>
      );
    }

    const nameFields = (
      <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
        <Box
          flex={1}
          minWidth={220}>
          <InputTextField
            id="payment-link-v2-billing-first-name"
            label={t('payment_link.form.first_name') || 'First name'}
            placeholder={t('payment_link.form.first_name') || 'First name'}
            maxLength={50}
            value={billing.firstName}
            setValue={(v: string) =>
              setField({firstName: validateNameInput(v)})
            }
            onBlur={() => setField({firstName: billing.firstName.trim()})}
          />
        </Box>
        <Box
          flex={1}
          minWidth={220}>
          <InputTextField
            id="payment-link-v2-billing-last-name"
            label={t('payment_link.form.last_name') || 'Last name'}
            placeholder={t('payment_link.form.last_name') || 'Last name'}
            maxLength={50}
            value={billing.lastName}
            setValue={(v: string) => setField({lastName: validateNameInput(v)})}
            onBlur={() => setField({lastName: billing.lastName.trim()})}
          />
        </Box>
      </Box>
    );

    const emailField = (
      <InputTextField
        id="payment-link-v2-billing-email"
        label={t('payment_link.form.email') || 'Email'}
        placeholder={t('payment_link.form.email') || 'Email'}
        type="email"
        value={billing.email}
        onErrorChange={setEmailError}
        setValue={(v: string) => setField({email: v})}
      />
    );

    const submitButton = (
      <Box
        className="billing-address-form-button"
        sx={{mt: 1}}>
        <AppButton
          id="payment-link-v2-billing-form-button"
          label={t('payment_link_v2.form.button')}
          disabled={!isFormValid || !!disableButtonContinue}
          variant={AppButtonVariant.onCheckout}
          onClick={handleSubmit}
        />
      </Box>
    );

    return (
      <Box
        className="billing-contact-form"
        sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        {nameFields}
        {emailField}

        <Typography className="billing-address-form-helper-text">
          {t('payment_link_v2.form.campaign_email_helper')}
        </Typography>

        {!simplified && (
          <PhoneInputTextField
            id="payment-link-v2-billing-phone"
            value={billing.phone}
            onChange={(v: string) => setField({phone: v})}
            countries={AllowedCountryCodesSignIn}
            showCountryCode={true}
          />
        )}

        <LoadScriptNext
          loadingElement={loadingGooglePlacesElement}
          googleMapsApiKey={ENV.VITE_GOOGLE_PLACES_KEY}
          libraries={libraries}>
          <Autocomplete
            onLoad={ac => (autoCompleteRef.current = ac)}
            options={{
              types: allowedAutocompleteTypes,
              componentRestrictions: {country: defaultAddressCountry},
            }}
            onPlaceChanged={handlePlaceChanged}
            className="billing-contact-autocomplete">
            <Box sx={{width: '100%'}}>
              <InputTextField
                id="payment-link-v2-billing-address-line-1"
                label={
                  t('payment_link.form.address_line_1_label') ||
                  'Address line 1'
                }
                placeholder={
                  t('payment_link.form.address_line_1_label') ||
                  'Address line 1'
                }
                value={billing.line1}
                setValue={(v: string) => setField({line1: v})}
                style={{width: '100%', fontSize: '16px'}}
              />
            </Box>
          </Autocomplete>
        </LoadScriptNext>

        <Typography className="billing-address-form-helper-text">
          {t('payment_link_v2.form.campaign_address_helper')}
        </Typography>

        <InputTextField
          id="payment-link-v2-billing-address-line-2"
          label={
            t('payment_link.form.address_line_2_label') || 'Address line 2'
          }
          placeholder={
            t('payment_link.form.address_line_2_label') || 'Address line 2'
          }
          maxLength={50}
          value={billing.line2}
          setValue={(v: string) => setField({line2: v})}
          style={{width: '100%', fontSize: '16px'}}
        />

        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
          <Box
            flex={1}
            minWidth={220}>
            <InputTextField
              id="payment-link-v2-billing-city"
              label={t('payment_link.form.city') || 'City'}
              placeholder={t('payment_link.form.city') || 'City'}
              value={billing.city}
              setValue={(v: string) => setField({city: v})}
              style={{width: '100%', fontSize: '16px'}}
            />
          </Box>
          <Box
            flex={1}
            minWidth={220}>
            <InputTextField
              id="payment-link-v2-billing-postal-code"
              label={t('payment_link.form.zip_code') || 'Postal code'}
              placeholder={t('payment_link.form.zip_code') || 'Postal code'}
              value={billing.postalCode}
              setValue={(v: string) => setField({postalCode: v})}
              style={{width: '100%', fontSize: '16px'}}
            />
          </Box>
        </Box>

        <InputTextField
          id="payment-link-v2-billing-state"
          label={t('payment_link.form.state') || 'State'}
          placeholder={t('payment_link.form.state') || 'State'}
          value={billing.state}
          setValue={(v: string) => setField({state: v})}
          style={{width: '100%', fontSize: '16px'}}
        />

        {submitButton}
      </Box>
    );
  },
);

export default BillingAddressForm;
